import type { EventBus } from '../kernel/events.js';
import type { Logger } from '../types/logger.js';
import { noOpLogger } from '../infrastructure/logger.js';
import { DefaultTaskStore, TaskTracker } from '../tasking/index.js';
import type { TaskNode } from '../types/task-graph.js';
import { toErrorMessage } from '../utils/error.js';
import type { WorktreeHandle, WorktreeManager } from '../worktree/worktree-manager.js';
import type {
  GoalOptions,
  PhaseEventMap,
  PhaseEventName,
  PhaseExecutionContext,
  PhaseGraph,
  PhaseNode,
  PhaseProgress,
  PhaseStatus,
} from './types.js';

export interface PhaseOrchestratorOptions extends GoalOptions {
  graph: PhaseGraph;
  ctx: PhaseExecutionContext;
  /** Structured logger. Defaults to noOpLogger (silent). */
  logger?: Logger | undefined;
}

type NormalizedGoalOptions = Omit<
  GoalOptions,
  | 'maxConcurrentPhases'
  | 'maxConcurrentTasks'
  | 'maxRetries'
  | 'maxVerifyAttempts'
  | 'autonomous'
  | 'phaseDelayMs'
  | 'stopOnFailure'
  | 'taskTimeoutMs'
  | 'events'
  | 'worktrees'
> & {
  maxConcurrentPhases: number;
  maxConcurrentTasks: number;
  maxRetries: number;
  maxVerifyAttempts: number;
  autonomous: boolean;
  phaseDelayMs: number;
  stopOnFailure: boolean;
  taskTimeoutMs: number;
  events: EventBus;
};

/**
 * PhaseOrchestrator - dependency-aware engine for running phases autonomously.
 *
 * Features:
 * - Automatically starts the next phase as each phase completes in autonomous mode
 * - Supports parallel phases with parallelizable=true
 * - Assigns and releases agents
 * - Integrates with the event bus
 * - Supports pause and resume
 */
export class PhaseOrchestrator {
  private graph: PhaseGraph;
  private ctx: PhaseExecutionContext;
  private opts: NormalizedGoalOptions;
  private events: EventBus;
  private stopped = false;
  private paused = false;
  private runningPhases = new Set<string>();
  private tickInterval: ReturnType<typeof setInterval> | null = null;
  private trackerCache = new Map<string, TaskTracker>();
  private taskRetryCounts = new Map<string, number>();

  // ── Git-worktree isolation (optional) ──────────────────────────────────────
  private readonly worktrees?: WorktreeManager | undefined;
  private readonly logger: Logger;
  /** Per-phase worktree handles, keyed by phase id. */
  private readonly phaseWorktrees = new Map<string, WorktreeHandle>();
  /** Serializes all merges back to the base branch (one at a time). */
  private mergeQueue: Promise<void> = Promise.resolve();
  /** Per-phase merge promise, so a phase merges only after its deps do. */
  private readonly phaseMergePromise = new Map<string, Promise<void>>();

  constructor(opts: PhaseOrchestratorOptions) {
    this.graph = opts.graph;
    this.ctx = opts.ctx;
    this.events = opts.events ?? this.createNoopEventBus();
    this.worktrees = opts.worktrees;
    this.logger = opts.logger ?? noOpLogger;
    this.opts = {
      maxConcurrentPhases: opts.maxConcurrentPhases ?? 1,
      maxConcurrentTasks: opts.maxConcurrentTasks ?? 2,
      maxRetries: opts.maxRetries ?? 2,
      maxVerifyAttempts: opts.maxVerifyAttempts ?? 2,
      autonomous: opts.autonomous ?? true,
      phaseDelayMs: opts.phaseDelayMs ?? 0,
      stopOnFailure: opts.stopOnFailure ?? false,
      taskTimeoutMs: opts.taskTimeoutMs ?? 600_000,
      events: this.events,
    };
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  /**
   * Start the full phase flow.
   * In autonomous mode, starts root phases and automatically starts the next phase when they finish.
   */
  async start(): Promise<void> {
    this.stopped = false;
    this.paused = false;
    this.normalizeForResume();
    this.graph.startedAt = Date.now();
    this.graph.updatedAt = Date.now();

    // Run phases in order; later phases still start when autonomous=false.
    let readyPhases = this.getReadyPhases();
    while (readyPhases.length > 0 && !this.stopped) {
      await this.waitWhilePaused();
      if (this.stopped) break;

      const batch = readyPhases.slice(0, this.opts.maxConcurrentPhases);
      await Promise.all(batch.map((p) => this.startPhase(p)));

      // Apply phase delay.
      if (this.opts.phaseDelayMs > 0) {
        await this.delay(this.opts.phaseDelayMs);
      }

      await this.waitWhilePaused();
      if (this.stopped) break;

      // Check for newly ready phases after a phase completes.
      readyPhases = this.getReadyPhases().filter(
        (p) => !this.runningPhases.has(p.id) && p.status !== 'completed' && p.status !== 'failed',
      );
    }

    // Wait for all queued worktree merges to finish in the background so
    // changes reach the base branch before the graph is declared completed.
    await this.drainMerges();

    // Autonomous tick loop for real-time monitoring.
    if (this.opts.autonomous) {
      this.tickInterval = setInterval(() => this.tick(), 1000);
    }
  }

  /**
   * Make a (possibly resumed) graph runnable. A graph loaded from disk after an
   * interrupted run can carry transient state from the dead process: phases left
   * `running` and tasks left `in_progress`. The scheduler only starts `pending`
   * phases (getReadyPhases) and only runs `pending` tasks (getExecutableTasks),
   * so without this a resumed phase/task would stall forever. Reset that
   * transient state to `pending`; terminal phases (completed/failed/skipped) and
   * already-completed tasks are untouched, so completed work is never re-run.
   * For a freshly built graph this is a no-op.
   */
  private normalizeForResume(): void {
    this.graph.activePhaseIds = []; // stale active ids from the previous process
    for (const phase of this.graph.phases.values()) {
      if (phase.status === 'running') {
        phase.status = 'pending';
        phase.updatedAt = Date.now();
      }
      if (phase.status === 'pending') {
        for (const task of phase.taskGraph.nodes.values()) {
          if (task.status === 'in_progress') task.status = 'pending';
        }
      }
    }
  }

  /** Wait for all pending phase merges, dependency-ordered and globally serialized. */
  private async drainMerges(): Promise<void> {
    await Promise.allSettled([...this.phaseMergePromise.values()]);
    await this.mergeQueue.catch((err) => {
      const msg = toErrorMessage(err);
      this.logger.warn(msg, { event: 'orchestrator.merge_queue_failed' });
    });
  }

  /** Pause: active phases continue, but no new phase starts. */
  pause(): void {
    this.paused = true;
  }

  /** Resume: new phases may start again. */
  resume(): void {
    this.paused = false;
    this.tick().catch((err) => {
      const msg = toErrorMessage(err);
      this.logger.error(msg, { event: 'orchestrator.tick_failed' });
    });
  }

  /** Stop completely, including active phases. */
  stop(): void {
    this.stopped = true;
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
    for (const phaseId of this.runningPhases) {
      const phase = this.graph.phases.get(phaseId);
      if (phase) {
        this.updatePhaseStatus(phase, 'paused');
      }
    }
    // Preserve any live worktrees for inspection rather than discarding work.
    if (this.worktrees) {
      for (const handle of this.worktrees.list()) {
        void this.worktrees.release(handle, { keep: true }).catch(() => {});
      }
    }
  }

  // ─── Tick Loop (Autonomous) ───────────────────────────────────────────────

  private async tick(): Promise<void> {
    if (this.stopped || this.paused) return;

    const active = this.getActivePhases();
    const queued = this.getReadyPhases();

    this.emit('autonomous.tick', {
      activePhases: active.map((p) => p.id),
      queuedPhases: queued.map((p) => p.id),
    });

    this.ctx.onTick?.({ activePhases: active, readyPhases: queued });

    // Is there a slot to start a new phase?
    const availableSlots = this.opts.maxConcurrentPhases - active.length;
    if (availableSlots > 0 && queued.length > 0) {
      for (const phase of queued.slice(0, availableSlots)) {
        if (phase.status === 'pending') {
          await this.startPhase(phase);
        }
      }
    }

    // Are all phases complete?
    if (this.isComplete()) {
      this.onGraphComplete();
      return;
    }

    // Did a phase fail while stopOnFailure is enabled?
    if (this.opts.stopOnFailure && this.graph.failedPhaseIds.length > 0) {
      const failedPhase = this.graph.phases.get(this.graph.failedPhaseIds[0] ?? '');
      if (failedPhase) {
        this.onGraphFailed(failedPhase);
      }
      return;
    }
  }

  // ─── Phase Execution ──────────────────────────────────────────────────────

  private async startPhase(phase: PhaseNode): Promise<void> {
    if (phase.status !== 'pending' && phase.status !== 'ready') return;

    this.updatePhaseStatus(phase, 'running');
    phase.startedAt = Date.now();
    this.runningPhases.add(phase.id);
    this.graph.activePhaseIds.push(phase.id);

    // Allocate an isolated git worktree for this phase, if a manager is wired.
    // Allocation failure degrades gracefully to the shared working tree.
    if (this.worktrees && !this.phaseWorktrees.has(phase.id)) {
      try {
        const handle = await this.worktrees.allocate(phase.id, {
          slugHint: phase.name,
          ownerLabel: phase.name,
        });
        if (handle.status === 'active') this.phaseWorktrees.set(phase.id, handle);
      } catch {
        // Manager already emitted worktree.failed; run on the shared tree.
      }
    }

    this.emit('phase.started', { phaseId: phase.id, name: phase.name });

    try {
      await this.executePhaseTasks(phase);

      const failedTasks = this.getFailedTaskCount(phase);
      const completedTasks = this.getCompletedTaskCount(phase);

      this.emit('phase.allTasksDone', {
        phaseId: phase.id,
        completed: completedTasks,
        failed: failedTasks,
      });

      if (failedTasks > 0 && this.opts.stopOnFailure) {
        await this.failPhaseAfterTasks(phase, `${failedTasks} task(s) failed`);
        return;
      }

      // Verification gate: all tasks succeeded, but the produced code must still
      // pass (typecheck/test/…) before we mark the phase done and merge it back.
      // Skipped entirely when no verifyPhase callback is wired (back-compat).
      const verdict = await this.runVerifyGate(phase);
      if (!verdict.ok) {
        await this.failPhaseAfterTasks(
          phase,
          `verification failed${verdict.output ? `: ${this.truncate(verdict.output)}` : ''}`,
        );
        return;
      }

      this.updatePhaseStatus(phase, 'completed');
      phase.completedAt = Date.now();
      phase.actualDurationMs = Date.now() - (phase.startedAt ?? Date.now());
      this.runningPhases.delete(phase.id);
      this.graph.activePhaseIds = this.graph.activePhaseIds.filter((id) => id !== phase.id);
      this.graph.completedPhaseIds.push(phase.id);
      this.emit('phase.completed', {
        phaseId: phase.id,
        name: phase.name,
        durationMs: phase.actualDurationMs,
      });
      this.ctx.onPhaseComplete?.(phase);
      // Commit the phase's work in its worktree and queue the merge back into
      // the base branch (dependency-ordered + globally serialized).
      await this.commitAndEnqueueMerge(phase);
    } catch (error) {
      this.updatePhaseStatus(phase, 'failed');
      phase.completedAt = Date.now();
      phase.actualDurationMs = Date.now() - (phase.startedAt ?? Date.now());
      this.runningPhases.delete(phase.id);
      this.graph.activePhaseIds = this.graph.activePhaseIds.filter((id) => id !== phase.id);
      this.graph.failedPhaseIds.push(phase.id);
      this.emit('phase.failed', {
        phaseId: phase.id,
        name: phase.name,
        error: error instanceof Error ? error.message : String(error),
      });
      this.ctx.onPhaseFail?.(phase, error instanceof Error ? error : new Error(String(error)));
      await this.keepWorktreeForReview(phase);
    }
  }

  // ─── Verification gate ──────────────────────────────────────────────────────

  /**
   * Run the verification gate for a phase whose tasks all succeeded. Verifies in
   * the phase's worktree; on failure, runs the repair pass and re-verifies, up to
   * `maxVerifyAttempts` repairs. Returns the final verdict. When no `verifyPhase`
   * callback is wired the gate is a no-op and always passes.
   */
  private async runVerifyGate(phase: PhaseNode): Promise<{ ok: boolean; output?: string | undefined }> {
    if (!this.ctx.verifyPhase) return { ok: true };
    const env = this.worktreeEnv(phase);

    for (let attempt = 0; attempt <= this.opts.maxVerifyAttempts; attempt++) {
      if (this.stopped) return { ok: false, output: 'stopped before verification completed' };

      this.emit('phase.verifying', { phaseId: phase.id, name: phase.name, attempt });
      let verdict: { ok: boolean; output?: string | undefined };
      try {
        verdict = await this.ctx.verifyPhase(phase, env);
      } catch (err) {
        verdict = { ok: false, output: toErrorMessage(err) };
      }
      if (verdict.ok) return { ok: true };

      this.emit('phase.verifyFailed', {
        phaseId: phase.id,
        name: phase.name,
        attempt,
        error: verdict.output,
      });

      // Out of attempts, no repair pass available, or aborted → give up.
      if (attempt >= this.opts.maxVerifyAttempts || !this.ctx.repairPhase || this.stopped) {
        return { ok: false, output: verdict.output };
      }

      this.emit('phase.repairing', { phaseId: phase.id, name: phase.name, attempt: attempt + 1 });
      try {
        await this.ctx.repairPhase(
          phase,
          verdict.output ?? 'verification failed',
          attempt + 1,
          env,
        );
      } catch {
        // A failed repair is non-fatal: the next verifyPhase run will observe the
        // still-broken tree and the loop will exit with ok:false.
      }
    }
    return { ok: false };
  }

  /** Worktree env (cwd/branch) for a phase, or undefined if it runs on the shared tree. */
  private worktreeEnv(phase: PhaseNode): { cwd?: string | undefined; branch?: string | undefined } | undefined {
    const handle = this.phaseWorktrees.get(phase.id);
    return handle ? { cwd: handle.dir, branch: handle.branch } : undefined;
  }

  /** Shared failure bookkeeping for a phase whose tasks ran but the phase failed. */
  private async failPhaseAfterTasks(phase: PhaseNode, error: string): Promise<void> {
    this.updatePhaseStatus(phase, 'failed');
    phase.completedAt = Date.now();
    phase.actualDurationMs = Date.now() - (phase.startedAt ?? Date.now());
    this.runningPhases.delete(phase.id);
    this.graph.activePhaseIds = this.graph.activePhaseIds.filter((id) => id !== phase.id);
    this.emit('phase.failed', { phaseId: phase.id, name: phase.name, error });
    this.ctx.onPhaseFail?.(phase, new Error(error));
    await this.keepWorktreeForReview(phase);
  }

  /**
   * A phase whose tasks all succeeded was marked `completed` and queued for
   * merge, but the merge back into the base branch failed. Its work is NOT
   * integrated, so correct the graph: move the phase out of `completedPhaseIds`
   * into `failedPhaseIds` and flip its status to `failed`. Without this the
   * persisted graph (and the board) would claim the phase succeeded while a
   * `phase.failed` event fired — an inconsistency that hides un-merged work.
   * Idempotent: safe to call more than once for the same phase.
   */
  private markPhaseMergeFailed(phase: PhaseNode, error: string): void {
    if (phase.status !== 'failed') {
      this.graph.completedPhaseIds = this.graph.completedPhaseIds.filter((id) => id !== phase.id);
      this.updatePhaseStatus(phase, 'failed');
    }
    if (!this.graph.failedPhaseIds.includes(phase.id)) this.graph.failedPhaseIds.push(phase.id);
    this.emit('phase.failed', { phaseId: phase.id, name: phase.name, error });
    this.ctx.onPhaseFail?.(phase, new Error(error));
  }

  /** Trim long verifier output so it fits cleanly in an event/error message. */
  private truncate(text: string, max = 500): string {
    const t = text.trim();
    return t.length <= max ? t : `${t.slice(0, max)}… (+${t.length - max} chars)`;
  }

  // ─── Worktree integration ───────────────────────────────────────────────────

  /**
   * Commit the phase's worktree changes, then enqueue the merge back into the
   * base branch. Merges run dependency-ordered (a phase merges only after its
   * `dependsOn` phases) and globally serialized (one at a time) to avoid
   * concurrent writes to the base tree.
   */
  private async commitAndEnqueueMerge(phase: PhaseNode): Promise<void> {
    const handle = this.phaseWorktrees.get(phase.id);
    if (!this.worktrees || !handle) return;

    try {
      await this.worktrees.commitAll(handle, `goal(${phase.name}): ${phase.id}`);
    } catch {
      // commit failure is non-fatal; the merge step will report a clean tree.
    }

    const depPromises = phase.dependsOn
      .map((d) => this.phaseMergePromise.get(d))
      .filter((p): p is Promise<void> => Boolean(p));

    const merged = (async () => {
      await Promise.allSettled(depPromises); // dependency-ordered
      // Chain onto the global queue so only one merge touches base at a time.
      this.mergeQueue = this.mergeQueue
        .then(() => this.mergeOne(phase, handle))
        .catch((err) => {
          // Defensive backstop: mergeOne handles its own errors, so this only
          // fires if it throws unexpectedly. Keep the queue alive (a failed
          // merge must not poison the chain) and correct the graph state.
          const msg = toErrorMessage(err);
          this.logger.error(msg, { event: 'orchestrator.merge_failed', phaseId: phase.id });
          this.markPhaseMergeFailed(phase, msg);
        });
      await this.mergeQueue;
    })();
    this.phaseMergePromise.set(phase.id, merged);
  }

  /**
   * Squash-merge one phase. When a `resolveConflict` callback is wired, a merge
   * conflict is handed to it (a resolver subagent) before giving up; only if
   * that fails does the worktree fall to needs-review and the run continues.
   */
  private async mergeOne(phase: PhaseNode, handle: WorktreeHandle): Promise<void> {
    if (!this.worktrees) return;
    try {
      const resolve = this.ctx.resolveConflict
        ? async (info: { conflictFiles: string[]; cwd: string }) => {
            const shouldResolve = await this.shouldAttemptConflictResolution(phase, info);
            if (!shouldResolve) return false;
            this.emit('phase.conflictResolving', {
              phaseId: phase.id,
              name: phase.name,
              files: info.conflictFiles,
            });
            const resolved = await this.ctx.resolveConflict?.(phase, info);
            return resolved ?? false;
          }
        : undefined;
      const mergeOpts: { squash: true; resolve?: (info: { conflictFiles: string[]; cwd: string }) => Promise<boolean> } = {
        squash: true,
      };
      if (resolve !== undefined) mergeOpts.resolve = resolve;
      const result = await this.worktrees.merge(handle, mergeOpts);
      if (result.resolved) {
        this.emit('phase.conflictResolved', { phaseId: phase.id, name: phase.name });
      }
      this.setIntegrationMetadata(phase, result.ok ? 'merged' : 'needs_review', {
        branch: handle.branch,
        worktreeDir: handle.dir,
        conflictFiles: result.conflictFiles,
      });
      // merge() already emitted worktree.merged / worktree.conflict and set status.
      // Clean (or resolved) merge → remove the worktree; conflict → release(keep).
      await this.worktrees.release(handle, { keep: !result.ok });
    } catch (err) {
      this.setIntegrationMetadata(phase, 'merge_failed', {
        branch: handle.branch,
        worktreeDir: handle.dir,
        error: toErrorMessage(err),
      });
      // The merge failed → the phase's work never reached base. Reflect that in
      // the graph, not just in metadata + a stray event (see markPhaseMergeFailed).
      this.markPhaseMergeFailed(phase, `worktree merge failed: ${toErrorMessage(err)}`);
    }
  }

  private async shouldAttemptConflictResolution(
    phase: PhaseNode,
    info: { conflictFiles: string[]; cwd: string },
  ): Promise<boolean> {
    if (!this.ctx.brain) return true;

    const decision = await this.ctx.brain.decide({
      id: `goal-conflict-${phase.id}`,
      source: 'goal',
      question: `Should Goal try to resolve merge conflicts for phase "${phase.name}" automatically?`,
      context: [
        `Phase id: ${phase.id}`,
        `Conflicted files: ${info.conflictFiles.join(', ') || '(unknown)'}`,
        `Base working tree: ${info.cwd}`,
      ].join('\n'),
      // 'critical', not 'high': choosing `resolve` lets a resolver agent EDIT
      // FILES IN THE BASE WORKING TREE. That is the one Brain decision in the
      // system whose wrong answer is not recoverable by simply continuing —
      // unlike a budget grant (bounded, capped) or a goal-done verdict (the
      // run can be restarted).
      //
      // Consequences of the level, both intended:
      //   - it is at or above the council floor, so a multi-LLM panel decides
      //     it whenever one can convene;
      //   - with no council (a single-model install resolves the autonomy
      //     ceiling to 'high'), it exceeds the ceiling and never reaches an
      //     unchecked single model. The policy tier's `ask_human` then routes
      //     to the terminal policy, which refuses a recommended option at
      //     critical risk and denies — i.e. the worktree is kept for human
      //     review, which is exactly the safe outcome.
      risk: 'critical',
      fallback: 'ask_human',
      options: [
        {
          id: 'resolve',
          label: 'Try the configured conflict resolver',
          consequence: 'A resolver agent may edit conflicted files in the base working tree.',
          risk: 'critical',
        },
        {
          id: 'review',
          label: 'Keep the worktree for human review',
          consequence: 'No automatic conflict resolution is attempted.',
          risk: 'low',
          recommended: true,
        },
      ],
    });

    phase.metadata = {
      ...phase.metadata,
      brainConflictDecision: decision.type,
      brainConflictDecisionAt: Date.now(),
    };

    if (decision.type !== 'answer') return false;
    // Control-plane check: automatic conflict resolution runs ONLY on the
    // exact option id. Prose mentioning "resolve" is not authorization.
    return decision.optionId === 'resolve';
  }

  private setIntegrationMetadata(
    phase: PhaseNode,
    status: 'merged' | 'needs_review' | 'merge_failed' | 'not_merged_failed_phase',
    details: {
      branch?: string | undefined;
      worktreeDir?: string | undefined;
      conflictFiles?: string[] | undefined;
      error?: string | undefined;
    } = {},
  ): void {
    phase.metadata = {
      ...phase.metadata,
      integrationStatus: status,
      integrationBranch: details.branch,
      integrationWorktreeDir: details.worktreeDir,
      integrationConflictFiles: details.conflictFiles,
      integrationError: details.error,
      integrationUpdatedAt: Date.now(),
    };
    phase.updatedAt = Date.now();
    this.graph.updatedAt = Date.now();
  }

  /** A failed phase keeps its worktree on disk for inspection (no merge). */
  private async keepWorktreeForReview(phase: PhaseNode): Promise<void> {
    const handle = this.phaseWorktrees.get(phase.id);
    if (!this.worktrees || !handle) return;
    try {
      await this.worktrees.commitAll(handle, `goal(${phase.name}) [failed]: ${phase.id}`);
    } catch {
      // best effort
    }
    this.setIntegrationMetadata(phase, 'not_merged_failed_phase', {
      branch: handle.branch,
      worktreeDir: handle.dir,
    });
    await this.worktrees.release(handle, { keep: true }).catch(() => {});
  }

  private async executePhaseTasks(phase: PhaseNode): Promise<void> {
    const pendingTasks = this.getExecutableTasks(phase);

    while (pendingTasks.length > 0 && !this.stopped) {
      const batch = pendingTasks.splice(0, this.opts.maxConcurrentTasks);

      const results = await Promise.allSettled(
        batch.map((task) => this.executeSingleTask(task, phase)),
      );

      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        const task = batch[i];
        if (!result || !task) continue;

        if (result.status === 'fulfilled') {
          this.markTaskCompleted(phase, task);
        } else {
          this.markTaskFailed(phase, task, result.reason);
        }
      }

      const newReady = this.getExecutableTasks(phase);
      pendingTasks.length = 0;
      pendingTasks.push(...newReady);
    }
  }

  private async executeSingleTask(task: TaskNode, phase: PhaseNode): Promise<unknown> {
    const tracker = this.getTrackerForPhase(phase);
    tracker.updateNodeStatus(task.id, 'in_progress');
    // Signal the start so boards can move the card to "in progress" and show the
    // worker. `executeTask` may assign/refine the agent right after (taskAssigned).
    this.emit('phase.taskStarted', {
      phaseId: phase.id,
      taskId: task.id,
      taskTitle: task.title,
      agentName: task.assignee,
    });
    const handle = this.phaseWorktrees.get(phase.id);
    const taskPromise = this.ctx.executeTask(task, phase.id, {
      cwd: handle?.dir,
      branch: handle?.branch,
    });
    // Apply per-task timeout when configured (taskTimeoutMs > 0).
    if (this.opts.taskTimeoutMs > 0) {
      const timeoutMs = this.opts.taskTimeoutMs;
      const timedOut = Symbol('timed_out');
      const result = await Promise.race([
        taskPromise,
        new Promise<typeof timedOut>((resolve) => {
          const timer = setTimeout(() => resolve(timedOut), timeoutMs);
          // Let the timer be freed if the task finishes first.
          taskPromise.then(() => clearTimeout(timer)).catch(() => clearTimeout(timer));
        }),
      ]);
      if (result === timedOut) {
        this.emit('phase.taskTimedOut', {
          phaseId: phase.id,
          taskId: task.id,
          taskTitle: task.title,
          timeoutMs,
        });
        throw new Error(
          `Task "${task.title}" (${task.id}) exceeded timeout of ${timeoutMs} ms`,
        );
      }
      return result;
    }
    return taskPromise;
  }

  private markTaskCompleted(phase: PhaseNode, task: TaskNode): void {
    const tracker = this.getTrackerForPhase(phase);
    tracker.updateNodeStatus(task.id, 'completed');
    this.emit('phase.taskCompleted', {
      phaseId: phase.id,
      taskId: task.id,
      taskTitle: task.title,
    });
  }

  private markTaskFailed(phase: PhaseNode, task: TaskNode, error: unknown): void {
    const tracker = this.getTrackerForPhase(phase);
    const taskKey = `${phase.id}:${task.id}`;
    const currentRetries = this.taskRetryCounts.get(taskKey) ?? 0;

    if (currentRetries < this.opts.maxRetries) {
      this.taskRetryCounts.set(taskKey, currentRetries + 1);
      tracker.updateNodeStatus(
        task.id,
        'pending',
        `Retry ${currentRetries + 1}/${this.opts.maxRetries}`,
      );
      this.emit('phase.taskRetrying', {
        phaseId: phase.id,
        taskId: task.id,
        taskTitle: task.title,
        attempt: currentRetries + 1,
        maxRetries: this.opts.maxRetries,
      });
    } else {
      tracker.updateNodeStatus(
        task.id,
        'failed',
        error instanceof Error ? error.message : String(error),
      );
      this.emit('phase.taskFailed', {
        phaseId: phase.id,
        taskId: task.id,
        taskTitle: task.title,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  private getReadyPhases(): PhaseNode[] {
    const ready: PhaseNode[] = [];

    for (const phase of this.graph.phases.values()) {
      if (phase.status !== 'pending') continue;

      const depsDone = phase.dependsOn.every((depId) => {
        const dep = this.graph.phases.get(depId);
        return dep?.status === 'completed' || dep?.status === 'skipped' || dep?.status === 'failed';
      });

      // A phase is ready ONLY when its dependencies are resolved. `parallelizable`
      // governs whether ready phases may run CONCURRENTLY (the `maxConcurrentPhases`
      // batch in start()/tick() already enforces that) — it must NOT let a phase
      // start before its dependencies finish. (Previously this read
      // `depsDone || phase.parallelizable`, which let a dependent-but-parallelizable
      // phase, e.g. a Testing phase depending on Implementation, jump the gun.)
      if (depsDone) {
        ready.push(phase);
      }
    }

    const prioOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    ready.sort((a, b) => (prioOrder[a.priority] ?? 4) - (prioOrder[b.priority] ?? 4));

    return ready;
  }

  private getActivePhases(): PhaseNode[] {
    return Array.from(this.graph.phases.values()).filter((p) => p.status === 'running');
  }

  private getExecutableTasks(phase: PhaseNode): TaskNode[] {
    const tracker = this.getTrackerForPhase(phase);
    return tracker
      .getAllNodes({ status: ['pending', 'blocked'] })
      .filter((n: { id: string; status: string; priority: string }) => n.status === 'pending' && tracker.canStart(n.id))
      .sort((a: { priority: TaskNode['priority'] }, b: { priority: TaskNode['priority'] }) => {
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        return (priorityOrder[a.priority] ?? 4) - (priorityOrder[b.priority] ?? 4);
      });
  }

  private getTrackerForPhase(phase: PhaseNode): TaskTracker {
    const cached = this.trackerCache.get(phase.id);
    if (cached) return cached;

    const tracker = new TaskTracker({ store: new DefaultTaskStore() });
    tracker.setGraph(phase.taskGraph);
    this.trackerCache.set(phase.id, tracker);
    return tracker;
  }

  private getFailedTaskCount(phase: PhaseNode): number {
    return this.getTrackerForPhase(phase).getAllNodes({ status: ['failed'] }).length;
  }

  private getCompletedTaskCount(phase: PhaseNode): number {
    return this.getTrackerForPhase(phase).getAllNodes({ status: ['completed'] }).length;
  }

  private updatePhaseStatus(phase: PhaseNode, status: PhaseStatus): void {
    const from = phase.status;
    phase.status = status;
    phase.updatedAt = Date.now();
    this.graph.updatedAt = Date.now();
    this.emit('phase.statusChange', { phaseId: phase.id, from, to: status });
  }

  private isComplete(): boolean {
    const allPhases = Array.from(this.graph.phases.values());
    return allPhases.every(
      (p) => p.status === 'completed' || p.status === 'skipped' || p.status === 'failed',
    );
  }

  private onGraphComplete(): void {
    this.graph.completedAt = Date.now();
    const durationMs = this.graph.completedAt - (this.graph.startedAt ?? this.graph.completedAt);
    this.emit('graph.completed', { graphId: this.graph.id, durationMs });
    this.stop();
  }

  private onGraphFailed(failedPhase: PhaseNode): void {
    this.emit('graph.failed', {
      graphId: this.graph.id,
      failedPhaseId: failedPhase.id,
      error: `Phase "${failedPhase.name}" failed`,
    });
    this.stop();
  }

  // ─── Progress ─────────────────────────────────────────────────────────────

  getProgress(): PhaseProgress {
    const phases = Array.from(this.graph.phases.values());
    let pending = 0;
    let ready = 0;
    let running = 0;
    let paused = 0;
    let completed = 0;
    let failed = 0;
    let skipped = 0;
    let totalTasks = 0;
    let completedTasks = 0;
    let failedTasks = 0;
    let estimatedHours = 0;
    let actualHours = 0;

    for (const p of phases) {
      switch (p.status) {
        case 'pending':
          pending++;
          break;
        case 'ready':
          ready++;
          break;
        case 'running':
          running++;
          break;
        case 'paused':
          paused++;
          break;
        case 'completed':
          completed++;
          break;
        case 'failed':
          failed++;
          break;
        case 'skipped':
          skipped++;
          break;
        default:
          // Unknown phase status — log and skip in counter
          break;
      }
      estimatedHours += p.estimateHours;
      if (p.actualDurationMs) actualHours += p.actualDurationMs / 3600000;

      const tracker = this.getTrackerForPhase(p);
      const progress = tracker.getProgress();
      totalTasks += progress.total;
      completedTasks += progress.completed;
      failedTasks += progress.failed;
    }

    const totalPhases = phases.length;
    const done = completed + skipped;

    return {
      totalPhases,
      pending,
      ready,
      running,
      paused,
      completed,
      failed,
      skipped,
      percentComplete: totalPhases > 0 ? Math.min(100, Math.round((done / totalPhases) * 100)) : 0,
      totalTasks,
      completedTasks,
      failedTasks,
      estimatedHours,
      actualHours,
    };
  }

  getGraph(): PhaseGraph {
    return this.graph;
  }

  isRunning(): boolean {
    return !this.stopped && this.runningPhases.size > 0;
  }

  isPaused(): boolean {
    return this.paused;
  }

  // ─── Agent Assignment ─────────────────────────────────────────────────────

  assignAgent(phaseId: string, agentId: string): void {
    const phase = this.graph.phases.get(phaseId);
    if (!phase) return;
    if (!phase.assignedAgents.includes(agentId)) {
      phase.assignedAgents.push(agentId);
      this.emit('agent.assigned', { phaseId, agentId });
    }
  }

  releaseAgent(phaseId: string, agentId: string): void {
    const phase = this.graph.phases.get(phaseId);
    if (!phase) return;
    phase.assignedAgents = phase.assignedAgents.filter((id) => id !== agentId);
    this.emit('agent.released', { phaseId, agentId });
  }

  // ─── Interactive board mutations ──────────────────────────────────────────
  //
  // These are driven by an interactive board (WebUI/TUI), not the autonomous
  // loop. Each mutates the live graph, emits a typed event so every surface
  // stays in sync, and bumps updatedAt so the host re-persists.

  /** Find the phase whose task graph currently holds `taskId`. */
  findPhaseOfTask(taskId: string): PhaseNode | undefined {
    for (const phase of this.graph.phases.values()) {
      if (phase.taskGraph.nodes.has(taskId)) return phase;
    }
    return undefined;
  }

  /**
   * Move a task to another phase's task graph. Edges that referenced the task
   * are dropped (cross-phase dependencies are not modeled). No-op when the task
   * or target phase is missing, or it is already in the target phase.
   */
  moveTask(taskId: string, toPhaseId: string): boolean {
    const from = this.findPhaseOfTask(taskId);
    const to = this.graph.phases.get(toPhaseId);
    if (!from || !to || from.id === toPhaseId) return false;

    const node = from.taskGraph.nodes.get(taskId);
    if (!node) return false;

    // Promote any task that depends on the moved task to a root node so it
    // doesn't stall as an orphaned blocked task in the source phase.
    const dependents = from.taskGraph.edges
      .filter((e) => e.from === taskId)
      .map((e) => e.to);
    for (const depId of dependents) {
      const depNode = from.taskGraph.nodes.get(depId);
      if (depNode) {
        depNode.parentId = undefined;
        depNode.children = undefined;
      }
      from.taskGraph.edges = from.taskGraph.edges.filter(
        (e) => !(e.from === taskId && e.to === depId),
      );
      if (!from.taskGraph.rootNodes.includes(depId)) {
        from.taskGraph.rootNodes.push(depId);
      }
    }

    // Detach from the source graph (nodes, rootNodes, touching edges).
    from.taskGraph.nodes.delete(taskId);
    from.taskGraph.rootNodes = from.taskGraph.rootNodes.filter((id) => id !== taskId);
    from.taskGraph.edges = from.taskGraph.edges.filter((e) => e.from !== taskId && e.to !== taskId);
    from.taskGraph.updatedAt = Date.now();

    // Attach to the target graph as a root node.
    node.parentId = undefined;
    node.children = undefined;
    node.updatedAt = Date.now();
    to.taskGraph.nodes.set(taskId, node);
    to.taskGraph.rootNodes.push(taskId);
    to.taskGraph.updatedAt = Date.now();

    // Invalidate cached trackers so getProgress/getExecutableTasks see the move.
    this.trackerCache.delete(from.id);
    this.trackerCache.delete(to.id);
    this.graph.updatedAt = Date.now();
    this.emit('phase.taskMoved', { taskId, fromPhaseId: from.id, toPhaseId });
    return true;
  }

  /** (Re)assign a task to a specific agent (or clear with agentName/agentId omitted). */
  setTaskAssignee(taskId: string, agentId?: string, agentName?: string): boolean {
    const phase = this.findPhaseOfTask(taskId);
    if (!phase) return false;
    const tracker = this.getTrackerForPhase(phase);
    tracker.updateNode(taskId, { assignee: agentName ?? agentId ?? '' });
    this.graph.updatedAt = Date.now();
    this.emit('phase.taskAssigned', { phaseId: phase.id, taskId, agentId, agentName });
    return true;
  }

  /** Add a new task to a phase. Returns the created task id, or null if the phase is missing. */
  addTask(
    phaseId: string,
    spec: {
      title: string;
      description?: string | undefined;
      type?: TaskNode['type'] | undefined;
      priority?: TaskNode['priority'] | undefined;
    },
  ): string | null {
    const phase = this.graph.phases.get(phaseId);
    if (!phase) return null;
    const tracker = this.getTrackerForPhase(phase);
    const node = tracker.addNode({
      title: spec.title,
      description: spec.description ?? '',
      type: spec.type ?? 'feature',
      priority: spec.priority ?? 'medium',
      status: 'pending',
    });
    this.graph.updatedAt = Date.now();
    this.emit('phase.taskAdded', { phaseId, taskId: node.id, taskTitle: node.title });
    return node.id;
  }

  /**
   * Requeue a task to `pending` (clearing its retry counter) and nudge a
   * terminal/paused phase back to `ready` so the loop re-runs it. Backs both the
   * board's "retry" and "start" affordances.
   */
  requeueTask(taskId: string): boolean {
    const phase = this.findPhaseOfTask(taskId);
    if (!phase) return false;
    const tracker = this.getTrackerForPhase(phase);
    tracker.updateNodeStatus(taskId, 'pending');
    this.taskRetryCounts.delete(`${phase.id}:${taskId}`);
    // A terminal/paused phase is reset to `pending` (the only status the
    // ready-scan + tick loop pick up) so its newly-pending task re-runs. Drop
    // the stale worktree handle so the rerun allocates a fresh isolated tree.
    if (phase.status === 'completed' || phase.status === 'failed' || phase.status === 'paused') {
      this.graph.failedPhaseIds = this.graph.failedPhaseIds.filter((id) => id !== phase.id);
      this.graph.completedPhaseIds = this.graph.completedPhaseIds.filter((id) => id !== phase.id);
      this.graph.activePhaseIds = this.graph.activePhaseIds.filter((id) => id !== phase.id);
      this.phaseWorktrees.delete(phase.id);
      this.updatePhaseStatus(phase, 'pending');
    }
    this.graph.updatedAt = Date.now();
    return true;
  }

  // ─── Events ───────────────────────────────────────────────────────────────

  private emit<K extends PhaseEventName>(event: K, payload: PhaseEventMap[K]): void {
    (this.events.emit as (event: string, payload: unknown) => void)(event, payload);
  }

  private createNoopEventBus(): EventBus {
    // Intentional test mock — safe because this is only called from test/benchmark code,
    // never with real event handling. Using `as never as EventBus` here is acceptable
    // since the return value is explicitly typed as EventBus and callers only use the
    // public API surface (which is fully implemented).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return {
      emit: () => {},
      on: () => () => {},
      off: () => {},
      once: () => () => {},
      listeners: new Map(),
      wildcards: [],
      setLogger: () => {},
      onAny: () => () => {},
      offAny: () => {},
      emitAsync: async () => [],
      waitFor: async () => {},
    } as never as EventBus;
  }

  private async waitWhilePaused(): Promise<void> {
    while (this.paused && !this.stopped) {
      await this.delay(100);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
